import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { MdOutlineDashboard } from "react-icons/md";
import { LuChevronRight } from "react-icons/lu";
import { usePermissions } from "hooks/usePermissions";
import { Tooltip } from "components/ui/tooltip";
import { SIDEBAR_SECTIONS } from "utils/sidebarConstants";
import { createPortal } from "react-dom";
import { PermissionAction, PermissionModule } from "models";

interface RouteItem {
  name: string;
  layout?: string;
  path?: string;
  sidebar?: boolean;
  module?: PermissionModule;
  icon?: React.ReactNode;
  children?: RouteItem[];
}

interface SidebarLinksProps {
  routes: RouteItem[];
  openSide: boolean;
}

// Path active matching helper
const isPathActive = (pathname: string, layout?: string, path?: string): boolean => {
  if (!layout || !path) return false;
  const normalizedPath = path.replace(/:[^/]+/g, "[^/]+");
  const regex = new RegExp(`^${layout}\/${normalizedPath}(\/|$)`);
  return regex.test(pathname);
};

// Check if any child route is active
const hasActiveChild = (pathname: string, children?: RouteItem[]): boolean => {
  if (!children || children.length === 0) return false;
  return children.some(
    (child) => child.path && isPathActive(pathname, child.layout, child.path)
  );
};

interface SidebarEllipsisTextProps {
  text: string;
  className: string;
}

const SidebarEllipsisText: React.FC<SidebarEllipsisTextProps> = React.memo(
  ({ text, className }) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isEllipsed, setIsEllipsed] = useState<boolean>(false);

    const checkOverflow = useCallback(() => {
      if (textRef.current) {
        setIsEllipsed(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    }, []);

    useEffect(() => {
      if (!textRef.current) return;
      const el = textRef.current;
      checkOverflow();

      if (typeof ResizeObserver !== "undefined") {
        const ro = new ResizeObserver(checkOverflow);
        ro.observe(el);
        return () => ro.disconnect();
      }
    }, [text, checkOverflow]);

    return (
      <Tooltip
        content={text}
        positioning={{ placement: "top", gutter: 8 }}
        showArrow
        openDelay={150}
        disabled={!isEllipsed}
      >
        <span
          ref={textRef}
          className={className}
          onMouseEnter={checkOverflow}
        >
          {text}
        </span>
      </Tooltip>
    );
  }
);

interface ParentRouteItemProps {
  route: RouteItem;
  openSide: boolean;
  isOpen: boolean;
  isParentActive: boolean;
  onToggle: (name: string) => void;
  pathname: string;
  hasPermission: (module: PermissionModule, action: PermissionAction) => boolean;
}

const ParentRouteItem = React.memo(({
  route,
  openSide,
  isOpen,
  isParentActive,
  onToggle,
  pathname,
  hasPermission,
}: ParentRouteItemProps) => {
  const [showFlyout, setShowFlyout] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleChildren = useMemo(() => {
    return (route.children || []).filter(
      (child) =>
        child.sidebar &&
        (!child.module || hasPermission(child.module, "READ"))
    );
  }, [route.children, hasPermission]);

  // Adjust flyout position in DOM without inline styles in JSX
  useEffect(() => {
    if (showFlyout && flyoutRef.current && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const flyoutHeight = flyoutRef.current.offsetHeight || 220;
      let topPos = rect.top;
      if (topPos + flyoutHeight > window.innerHeight - 10) {
        topPos = Math.max(10, window.innerHeight - flyoutHeight - 10);
      }
      flyoutRef.current.style.top = `${topPos}px`;
    }
  }, [showFlyout]);

  const handleMouseEnter = useCallback(() => {
    if (openSide) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowFlyout(true);
  }, [openSide]);

  const handleMouseLeave = useCallback(() => {
    if (openSide) return;
    timeoutRef.current = setTimeout(() => {
      setShowFlyout(false);
    }, 150);
  }, [openSide]);

  const handleToggle = useCallback(() => {
    if (openSide) {
      onToggle(route.name);
    } else {
      setShowFlyout((prev) => !prev);
    }
  }, [openSide, onToggle, route.name]);

  const handleCloseFlyout = useCallback(() => {
    setShowFlyout(false);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (visibleChildren.length === 0) return null;

  return (
    <div
      className="sidebar-parent-group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={`sidebar-nav-item ${isParentActive ? "is-active" : ""} ${
          openSide ? "" : "is-collapsed"
        }`}
        aria-expanded={openSide ? isOpen : showFlyout}
      >
        {isParentActive && <span className="sidebar-active-bar" />}
        {openSide ? (
          <>
            <div className="sidebar-item-content">
              <span className="sidebar-icon">
                {route.icon || <MdOutlineDashboard />}
              </span>
              <SidebarEllipsisText text={route.name} className="sidebar-text" />
            </div>
            <span className={`sidebar-chevron ${isOpen ? "is-rotated" : ""}`}>
              <LuChevronRight />
            </span>
          </>
        ) : (
          <div className="sidebar-collapsed-item-content">
            <span className="sidebar-icon">
              {route.icon || <MdOutlineDashboard />}
            </span>
            <span
              className="sidebar-collapsed-submenu-indicator"
              aria-hidden="true"
            >
              <LuChevronRight />
            </span>
          </div>
        )}
      </button>

      {/* Expanded Submenu Accordion Tree */}
      {openSide && isOpen && (
        <div className="sidebar-submenu-wrapper">
          <div className="sidebar-submenu-tree">
            {visibleChildren.map((child, childIdx) => {
              if (!child.path) return null;
              const isChildActive = isPathActive(
                pathname,
                child.layout,
                child.path
              );

              return (
                <Link
                  key={childIdx}
                  to={`${child.layout}/${child.path}`}
                  className="sidebar-child-link"
                >
                  <div
                    className={`sidebar-child-item ${
                      isChildActive ? "is-active" : ""
                    }`}
                  >
                    {isChildActive && (
                      <span className="sidebar-child-active-bar" />
                    )}
                    <span className="sidebar-child-icon">
                      {child.icon || <MdOutlineDashboard />}
                    </span>
                    <SidebarEllipsisText
                      text={child.name}
                      className="sidebar-child-text"
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Compact Flyout Popover Menu */}
      {!openSide &&
        showFlyout &&
        createPortal(
          <div
            ref={flyoutRef}
            className="sidebar-flyout-menu"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="sidebar-flyout-header">{route.name}</div>
            <div className="sidebar-flyout-list">
              {visibleChildren.map((child, childIdx) => {
                if (!child.path) return null;
                const isChildActive = isPathActive(
                  pathname,
                  child.layout,
                  child.path
                );

                return (
                  <Link
                    key={childIdx}
                    to={`${child.layout}/${child.path}`}
                    className="sidebar-flyout-link"
                    onClick={handleCloseFlyout}
                  >
                    <div
                      className={`sidebar-flyout-item ${
                        isChildActive ? "is-active" : ""
                      }`}
                    >
                      {isChildActive && (
                        <span className="sidebar-flyout-active-bar" />
                      )}
                      <span className="sidebar-flyout-icon">
                        {child.icon || <MdOutlineDashboard />}
                      </span>
                      <span className="sidebar-flyout-text">
                        {child.name}
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
});

interface SingleRouteItemProps {
  route: RouteItem;
  openSide: boolean;
  isActive: boolean;
}

const SingleRouteItem = React.memo(({
  route,
  openSide,
  isActive,
}: SingleRouteItemProps) => {
  const navItem = (
    <div
      className={`sidebar-nav-item ${isActive ? "is-active" : ""} ${
        openSide ? "" : "is-collapsed"
      }`}
    >
      {isActive && <span className="sidebar-active-bar" />}
      {openSide ? (
        <div className="sidebar-item-content">
          <span className="sidebar-icon">
            {route.icon || <MdOutlineDashboard />}
          </span>
          <SidebarEllipsisText text={route.name} className="sidebar-text" />
        </div>
      ) : (
        <span className="sidebar-icon">
          {route.icon || <MdOutlineDashboard />}
        </span>
      )}
    </div>
  );

  return (
    <Link
      to={`${route.layout}/${route.path}`}
      className="sidebar-child-link"
    >
      {openSide ? (
        navItem
      ) : (
        <Tooltip
          content={route.name}
          positioning={{ placement: "right", gutter: 12 }}
          showArrow
        >
          {navItem}
        </Tooltip>
      )}
    </Link>
  );
});

export const SidebarLinks = ({
  routes,
  openSide,
}: SidebarLinksProps): React.ReactElement => {
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const [openMenus, setOpenMenus] = useState<string[]>([]);

  // Automatically expand parent menu if current location matches any of its children
  useEffect(() => {
    routes.forEach((route) => {
      if (
        route.layout === "/superuser" &&
        route.children &&
        hasActiveChild(location.pathname, route.children)
      ) {
        setOpenMenus((prev) =>
          prev.includes(route.name) ? prev : [...prev, route.name]
        );
      }
    });
  }, [location.pathname, routes]);

  const toggleMenu = useCallback((name: string) => {
    setOpenMenus((prev) =>
      prev.includes(name)
        ? prev.filter((item) => item !== name)
        : [...prev, name]
    );
  }, []);

  // Group routes into sections according to reference design
  const sectionedGroups = useMemo(() => {
    const visibleRoutes = routes.filter((route) => {
      if (route.layout !== "/superuser") return false;
      if (route.children && route.children.length > 0) {
        if (route.sidebar === false) return false;
        const visibleChildren = route.children.filter(
          (c) => c.sidebar && (!c.module || hasPermission(c.module, "READ"))
        );
        return visibleChildren.length > 0;
      }
      if (!route.path || !route.sidebar) return false;
      if (route.module && !hasPermission(route.module, "READ")) return false;
      return true;
    });

    const grouped = SIDEBAR_SECTIONS.map((section) => {
      const sectionRoutes = visibleRoutes.filter((r) =>
        section.routeNames.includes(r.name)
      );
      return {
        ...section,
        routes: sectionRoutes,
      };
    }).filter((section) => section.routes.length > 0);

    // Any routes not configured in SIDEBAR_SECTIONS
    const assignedNames = new Set(
      SIDEBAR_SECTIONS.flatMap((s) => s.routeNames)
    );
    const unassignedRoutes = visibleRoutes.filter(
      (r) => !assignedNames.has(r.name)
    );

    if (unassignedRoutes.length > 0) {
      grouped.push({
        id: "other",
        header: "OTHER",
        routeNames: unassignedRoutes.map((r) => r.name),
        routes: unassignedRoutes,
      });
    }

    return grouped;
  }, [routes, hasPermission]);

  return (
    <>
      {sectionedGroups.map((section, sectionIdx) => (
        <div key={section.id} className="sidebar-section-group">
          {/* Subtle divider before subsequent sections */}
          {sectionIdx > 0 && <div className="sidebar-section-divider" />}

          {/* Section Header */}
          {openSide && (
            <div className="sidebar-section-header">{section.header}</div>
          )}

          {/* Route items inside this section */}
          {section.routes.map((route, routeIdx) => {
            if (route.children && route.children.length > 0) {
              const isChildActive = hasActiveChild(
                location.pathname,
                route.children
              );
              const isDirectParentActive = route.path
                ? isPathActive(location.pathname, route.layout, route.path)
                : false;
              const isParentActive = openSide
                ? isDirectParentActive
                : isDirectParentActive || isChildActive;

              return (
                <ParentRouteItem
                  key={`${section.id}-${routeIdx}`}
                  route={route}
                  openSide={openSide}
                  isOpen={openMenus.includes(route.name)}
                  isParentActive={isParentActive}
                  onToggle={toggleMenu}
                  pathname={location.pathname}
                  hasPermission={hasPermission}
                />
              );
            }

            const isActiveRoute = isPathActive(
              location.pathname,
              route.layout,
              route.path
            );

            return (
              <SingleRouteItem
                key={`${section.id}-${routeIdx}`}
                route={route}
                openSide={openSide}
                isActive={isActiveRoute}
              />
            );
          })}
        </div>
      ))}
    </>
  );
};

export default SidebarLinks;
